
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

import {
  Users,
  Settings,
  MessageSquare,
  ArrowLeft,
  CreditCard,
  ShoppingBag,
  Cookie,
  BarChart3,
  FileBadge,
  ListFilter,
  Menu,
  Download,
  RefreshCw,
  LineChart,
  AlertTriangle
} from "lucide-react";

import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { invokeFunction } from "@/lib/utils/invokeFunction";

import UserManagement from "@/components/admin/UserManagement";
import CookieConsents from "@/components/admin/CookieConsents";
import OrdersManagement from "@/components/admin/OrdersManagement";
import CoinsManagement from "@/components/admin/CoinsManagement";
import SubscriptionManagement from "@/components/admin/SubscriptionManagement";
import { AdminChats } from "@/components/admin/AdminChats";
import TermsPrivacyConsents from "@/components/admin/TermsPrivacyConsents";
import VoucherManagement from "@/components/admin/VoucherManagement";
import ListingApproval from "@/components/admin/ListingApproval";
import CronJobsManager from "@/components/admin/CronJobsManager";
import DashboardStatistics from "@/components/admin/DashboardStatistics";
import PwaInstallStats from "@/components/admin/PwaInstallStats";
import SiteStatistics from "@/components/admin/SiteStatistics";
import { SystemErrors } from "@/components/admin/SystemErrors";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { profile, isAuthenticated } = useAuth();
  const isAdmin = profile?.role === "admin";
  const [activePage, setActivePage] = useState("stats");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    
    // Check for cleanup function
    const cleanupLastRun = localStorage.getItem("cleanup_last_run");
    if (cleanupLastRun !== today) {
      invokeFunction("cleanup-old-listings")
        .then(({ data, error }) => {
          console.log("Cleanup Function Result:", data);
          if (error) console.error("Cleanup Function Error:", error);
          localStorage.setItem("cleanup_last_run", today);
        });
    }
    
    // Check for permanent deletion function
    const permanentDeleteLastRun = localStorage.getItem("permanent_delete_last_run");
    if (permanentDeleteLastRun !== today) {
      invokeFunction("permanent-delete-old-listings")
        .then(({ data, error }) => {
          console.log("Permanent Delete Function Result:", data);
          if (error) console.error("Permanent Delete Function Error:", error);
          localStorage.setItem("permanent_delete_last_run", today);
        });
    }

    // Check for credit reset function
    const creditResetLastRun = localStorage.getItem("credit_reset_last_run");
    if (creditResetLastRun !== today) {
      invokeFunction("refresh-free-credits")
        .then(({ data, error }) => {
          console.log("Refresh Free Credits Function Result:", data);
          if (error) console.error("Refresh Free Credits Function Error:", error);
          // @ts-ignore
          if (data?.success) {
            localStorage.setItem("credit_reset_last_run", today);
          }
        });
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate("/");
    }
  }, [isAuthenticated, isAdmin, navigate]);

  const handleNavigateBack = () => {
    navigate("/profile");
  };

  if (!isAuthenticated || !isAdmin) return null;

  const renderActivePage = () => {
    switch (activePage) {
      case "stats":
        return <DashboardStatistics />;
      case "errors":
        return <SystemErrors />;
      case "users":
        return <UserManagement />;
      case "orders":
        return <OrdersManagement />;
      case "subscriptions":
        return <SubscriptionManagement />;
      case "coins":
        return <CoinsManagement />;
      case "chats":
        return <AdminChats />;
      case "cookies":
        return <CookieConsents />;
      case "terms":
        return <TermsPrivacyConsents />;
      case "vouchers":
        return <VoucherManagement />;
      case "listings":
        return <ListingApproval />;
      case "crons":
        return <CronJobsManager />;
      case "pwa-stats": 
        return <PwaInstallStats />;
      case "site-stats":
        return <SiteStatistics />;
      default:
        return <DashboardStatistics />;
    }
  };

  return (
    <Container>
      <div className="py-4 sm:py-8 px-2 sm:px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleNavigateBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>

        <div className="flex">
          {/* Sidebar Menu */}
          {menuOpen && (
            <aside className="w-64 bg-gray-100 p-4 rounded-lg shadow-md">
              <nav className="flex flex-col space-y-2">
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("stats")}
                  className="flex items-center gap-2"
                >
                  <BarChart3 className="h-5 w-5" />
                  Statistics
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("errors")}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  <AlertTriangle className="h-5 w-5" />
                  System Errors
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("site-stats")}
                  className="flex items-center gap-2"
                >
                  <LineChart className="h-5 w-5" />
                  Site Traffic
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("users")}
                  className="flex items-center gap-2"
                >
                  <Users className="h-5 w-5" />
                  Users
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("orders")}
                  className="flex items-center gap-2"
                >
                  <ShoppingBag className="h-5 w-5" />
                  Orders
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("subscriptions")}
                  className="flex items-center gap-2"
                >
                  <CreditCard className="h-5 w-5" />
                  Subscriptions
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("coins")}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-5 w-5" />
                  Coins & Credits
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("chats")}
                  className="flex items-center gap-2"
                >
                  <MessageSquare className="h-5 w-5" />
                  Chats
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("cookies")}
                  className="flex items-center gap-2"
                >
                  <Cookie className="h-5 w-5" />
                  Cookies
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("terms")}
                  className="flex items-center gap-2"
                >
                  <FileBadge className="h-5 w-5" />
                  Terms
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("vouchers")}
                  className="flex items-center gap-2"
                >
                  <Settings className="h-5 w-5" />
                  Vouchers
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("listings")}
                  className="flex items-center gap-2"
                >
                  <ListFilter className="h-5 w-5" />
                  Listings
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("crons")}
                  className="flex items-center gap-2"
                >
                  <Settings className="h-5 w-5" />
                  Cron Jobs
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setActivePage("pwa-stats")}
                  className="flex items-center gap-2"
                >
                  <Download className="h-5 w-5" /> 
                  App Installs
                </Button>
              </nav>
            </aside>
          )}

          {/* Main Content */}
          <main className="flex-grow ml-4">
            <Card className="p-4 shadow-md">{renderActivePage()}</Card>
          </main>
        </div>
      </div>
    </Container>
  );
};

export default AdminDashboard;
